import { useState } from 'react'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Button, F0TagRaw, TwoColumnLayout } from '@factorialco/f0-react'
import { Dropdown, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { F0TableOfContent } from '@factorialco/f0-react/dist/experimental'
import { Add, Ellipsis, Pencil, Placeholder } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import ContentEditorModal from 'modules/trainings/components/content/ContentEditorModal'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'

type ModalState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; item: ContentItem }

type TocItem = {
  id: string
  label: string
  onClick: () => void
  children?: TocItem[]
}

interface BuilderProps {
  tocItems: TocItem[]
  selectedItem?: ContentItem
  trainingId: number
  allowedContentTypes?: string[]
  onReorder: (orderedStructure: Array<{ id: string; children?: Array<{ id: string }> }>) => void
  onAddContent: (params: { contentItem: ContentItem }) => void
  onEditContent: (params: { uuid: string; contentItem: ContentItem }) => void
  onDeleteContent: (uuid: string) => void
}

const ContentBuilder = ({
  tocItems,
  selectedItem,
  trainingId,
  allowedContentTypes,
  onReorder,
  onAddContent,
  onEditContent,
  onDeleteContent,
}: BuilderProps) => {
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' })

  return (
    <>
      <Divider />
      <TwoColumnLayout
        mainColumnPosition="right"
        sideContent={
          <div>
            <F0TableOfContent
              activeItem={selectedItem?.uuid}
              items={tocItems}
              title={i18n.t('trainings.content.builder.title')}
              sortable
              onReorder={(reorderedIds) => {
                onReorder(reorderedIds)
              }}
            />
            <F0Button
              onClick={() => setModalState({ mode: 'create' })}
              icon={Add}
              variant="ghost"
              label={i18n.t('trainings.content.builder.add')}
            />
          </div>
        }
      >
        {!selectedItem ? (
          <Box alignItems="center" justifyContent="center" height="full">
            <OneEmptyState
              emoji="📚"
              title={i18n.t('trainings.content.builder.empty.header')}
              description={i18n.t('trainings.content.builder.empty.description')}
              actions={[
                {
                  icon: Add,
                  label: i18n.t('trainings.content.builder.empty.button'),
                  onClick: () => setModalState({ mode: 'create' }),
                  variant: 'outline',
                },
              ]}
            />
          </Box>
        ) : (
          <Box alignItems="center">
            <Box
              flexDirection="row"
              alignSelf="start"
              justifyContent="spaceBetween"
              width="full"
              height="s64"
            >
              <div>
                <ItemTag item={selectedItem} />
              </div>
              <div>
                <Dropdown
                  icon={Ellipsis}
                  items={[
                    {
                      icon: Pencil,
                      label: i18n.t('common.edit'),
                      onClick: () => {
                        if (selectedItem) {
                          setModalState({ mode: 'edit', item: selectedItem })
                        }
                      },
                    },
                    {
                      icon: Placeholder,
                      label: i18n.t('common.delete'),
                      critical: true,
                      onClick() {
                        if (selectedItem) {
                          onDeleteContent(selectedItem.uuid)
                        }
                      },
                    },
                  ]}
                />
              </div>
            </Box>
            <RenderItem item={selectedItem} />
          </Box>
        )}
      </TwoColumnLayout>

      {modalState.mode !== 'closed' && (
        <ContentEditorModal
          item={modalState.mode === 'edit' ? modalState.item : undefined}
          onClose={() => setModalState({ mode: 'closed' })}
          onSave={(item: ContentItem) => {
            if (modalState.mode === 'create') {
              onAddContent({
                contentItem: item,
              })
            } else if (modalState.mode === 'edit') {
              onEditContent({ uuid: modalState.item.uuid, contentItem: item })
            }
            setModalState({ mode: 'closed' })
          }}
          trainingId={trainingId}
          allowedContentTypes={allowedContentTypes || []}
        />
      )}
    </>
  )
}

function ItemTag({ item }: { item: ContentItem }) {
  const icon = contentRegistry.getIconForItem(item)
  const label = contentRegistry.getLabelForItem(item)

  if (icon && label) {
    return <F0TagRaw icon={icon} text={label} />
  }

  return null
}

function RenderItem({ item }: { item: ContentItem }) {
  const ViewComponent = contentRegistry.getViewForItem(item)

  if (!ViewComponent) {
    return <div>Unknown content type: {item.constructor.name}</div>
  }

  return <ViewComponent contentItem={item} />
}

export default ContentBuilder
